import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { splitLines, SplitTextEngine } from '@/utils/split-text'

export type SharedAnimation = {
  timeline: gsap.core.Timeline
  revert: () => void
}

export const getScrollTriggerOptions = (
  node: HTMLElement,
  reverse: boolean,
  start = 'top 82%' // 'top 92%'
) => {
  return {
    scrollTrigger: {
      trigger: node,
      once: !reverse,
      start,
      markers: false,
      toggleActions: `play none none ${reverse ? 'reverse' : 'none'}`
    }
  }
}

export const linesRevealAnimation = (
  target: HTMLElement,
  reverse?: boolean,
  splitTextEngine: SplitTextEngine = SplitTextEngine.GSAP
): SharedAnimation => {
  const splitText = splitLines(target, splitTextEngine)

  return {
    timeline: gsap
      .timeline({
        onStart: () => {
          target.classList.add('reveal')
        },
        onComplete: () => {
          requestAnimationFrame(() => {
            target.classList.remove('reveal')
          })
          if (reverse) return
          splitText.revert()
        }
      })
      .fromTo(
        splitText.lines,
        { yPercent: 120 },
        {
          yPercent: 0,
          ease: 'power2.out',
          duration: 0.8,
          stagger: splitText.getStagger(0.1)
        },
        '<'
      ),

    revert: () => {
      splitText.revert()
    }
  }
}

export const paragraphRevealAnimation = (
  target: HTMLElement,
  reverse: boolean,
  splitTextEngine: SplitTextEngine = SplitTextEngine.GSAP
): SharedAnimation => {
  const splitText = splitLines(target, splitTextEngine)

  return {
    timeline: gsap
      .timeline({
        onComplete: () => {
          if (reverse) return
          splitText.revert()
        }
      })
      .fromTo(
        splitText.instance.lines,
        { yPercent: 120, opacity: 0 },
        {
          opacity: 1,
          yPercent: 0,
          ease: 'power2.out',
          duration: 0.8,
          stagger: splitText.getStagger(0.1)
        }
      ),

    revert: () => {
      splitText.revert()
    }
  }
}

export const mediaZoomRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { scale: 1.15, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.6, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const mediaFadeRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'power2.inOut' }
      ),

    revert: () => {
      return
    }
  }
}

export const fadeRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const opacityRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const batchRevealAnimation = ({
  targets,
  reverse,
  stagger,
  delay
}: {
  targets: gsap.DOMTarget
  reverse: boolean
  stagger?: gsap.NumberValue | gsap.StaggerVars
  delay?: number
}) => {
  gsap.set(targets, { opacity: 0, y: 30 })

  return ScrollTrigger.batch(targets, {
    // interval: 0.1, // time window (in seconds) for batching to occur.
    // batchMax: 3,   // maximum batch size (targets)
    once: !reverse,
    start: 'top 82%',
    onEnter: (batch) =>
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power2.out',
        stagger,
        delay
      })
  })
}
